import { createContext, use } from 'react';

import { type KeyedRegistry, useRegisterWithGetter } from '#core/hooks/useKeyedRegistry.ts';

/**
 * One downloadable artifact contributed by a tile (or by a nested dashboard,
 * when this registry is itself nested inside a message-level registry).
 *
 * `getCsv` is called at click-time on the dashboard's download button.
 */
export interface TileExportEntry {
  readonly id: string;
  readonly name: string;
  readonly getCsv: () => string;
}

export const DashboardExportContext = createContext<KeyedRegistry<TileExportEntry> | null>(null);

/**
 * Register one tile's CSV producer with the enclosing dashboard. Pass
 * `null` for tiles that have no downloadable data (e.g. metric/KPI) — the
 * hook stays in the render path (rules of hooks) but does nothing.
 *
 * `getCsv` is shielded through `useRegisterWithGetter` so the registered
 * entry resolves to the latest closure at click time without re-registering
 * every time the panel's data re-renders.
 */
export function useRegisterTileExport(entry: TileExportEntry | null): void {
  const ctx = use(DashboardExportContext);
  useRegisterWithGetter(ctx, entry, 'getCsv', (e) => e.getCsv());
}
